import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Flame, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatUsd, EMPTY_BOARD_ENTRY_CENTS } from "@/lib/behot";
import type { Category } from "@/lib/queries";
import { useT } from "@/i18n";

function countdown(target: string | null) {
  if (!target) return null;
  const ms = new Date(target).getTime() - Date.now();
  if (!Number.isFinite(ms) || ms <= 0) return null;
  const d = Math.floor(ms / 86_400_000);
  const h = Math.floor((ms % 86_400_000) / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  const s = Math.floor((ms % 60_000) / 1000);
  return d > 0 ? `${d}d ${h}h ${m}m` : `${h}h ${m}m ${s}s`;
}

/**
 * Green "be first" banner shown while a board is empty after a season refresh:
 * position 01 is open and entry costs the category opening price.
 */
export function BoardOpeningBanner({
  category,
  isEmpty,
  compact = false,
}: {
  category: Category;
  isEmpty: boolean;
  compact?: boolean;
}) {
  const { t } = useT();
  // Starts empty on purpose: a seconds-precision countdown rendered during SSR
  // never matches the first client render and breaks hydration.
  const [left, setLeft] = useState<string | null>(null);

  useEffect(() => {
    setLeft(countdown(category.season_ends_at));
    const t2 = setInterval(() => setLeft(countdown(category.season_ends_at)), 1000);
    return () => clearInterval(t2);
  }, [category.season_ends_at]);

  if (!isEmpty || !category.opening_banner_enabled) return null;

  // Empty / freshly refreshed boards always open at the platform entry price.
  const price = formatUsd(EMPTY_BOARD_ENTRY_CENTS);


  if (compact) {
    return (
      <p className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-center text-xs font-semibold text-emerald-700 dark:text-emerald-300">
        {t("boards.opening.compact", { price })}
      </p>
    );
  }

  return (
    <div className="mt-5 rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-4 sm:mt-6 sm:p-5">
      <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-4 sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:items-center">
        <Sparkles className="order-2 size-5 shrink-0 text-emerald-600 sm:order-1 dark:text-emerald-400" />
        <div className="order-1 min-w-0 sm:order-2">
          <p className="font-display text-sm font-bold leading-snug text-emerald-800 sm:text-base dark:text-emerald-200">
            {t("boards.opening.seasonOpened", { n: category.season_number })}
          </p>
          <p className="mt-1.5 text-sm leading-relaxed text-emerald-800/80 dark:text-emerald-200/80">
            {t("boards.opening.refreshedIntro")}{" "}
            <strong>{price}</strong>. {t("boards.opening.afterFirstTaken", { name: category.name })}
            {left ? ` ${t("boards.opening.seasonCloses", { left })}` : ""}
          </p>
        </div>
        <div className="order-3 col-span-2 grid min-w-0 grid-cols-2 gap-2 sm:col-span-1 sm:flex sm:shrink-0">
          <Button asChild variant="hot" size="sm" className="min-w-0 px-2.5 sm:px-3">
            <Link to="/get-hot" search={{ category: category.slug, listing: undefined }}>
              <Flame className="size-4 shrink-0" /> <span className="truncate">{t("boards.opening.takePosition")}</span>
            </Link>
          </Button>
          <Button asChild variant="outline" size="sm" className="min-w-0 px-2.5 sm:px-3">
            <Link to="/app/wallets" className="truncate">{t("boards.opening.topUpHeat")}</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
